import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSearchLocation } from '../contexts/LocationContext';
import AuthModal from './AuthModal';
import LocationPicker from './LocationPicker';
import { DISCOUNTS_ENABLED } from '../content/features';
import './Navbar.css';

export default function Navbar() {
  const { user, userProfile, isAdmin, isRestaurantOwner, logout, isGuest, guestProfile, openClaim } = useAuth();
  // A guest who has posted gets "Claim account" where Sign In would be; the
  // dialog it opens has a Sign In tab too, and either keeps their posts.
  const guestWithPosts = isGuest && !!guestProfile;
  const { location: searchLocation, setLocation: setSearchLocation } = useSearchLocation();
  const [authModal, setAuthModal] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { pathname, state: routeState } = useLocation();
  // Venue pages live at /business/:id too, so NavLink's prefix match would
  // light up "For Businesses" on them. They belong to Explore instead.
  const inBusinessArea = pathname === '/business' || /^\/business\/(onboarding|dashboard|scan)(\/|$)/.test(pathname);
  const onExplore = pathname === '/' || (pathname.startsWith('/business/') && !inBusinessArea);
  useEffect(() => { if (routeState?.signIn) setAuthModal('signin'); }, [routeState]);

  useEffect(() => { setMobileOpen(false); }, [pathname]);
  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const links = () => [toggleRef.current, ...menuRef.current.querySelectorAll('a, button')].filter(Boolean);
    const keydown = event => {
      if (event.key === 'Escape') { setMobileOpen(false); toggleRef.current?.focus(); }
      if (event.key === 'Tab') {
        const items = links(), first = items[0], last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    const resize = () => { if (window.innerWidth > 768) setMobileOpen(false); };
    document.addEventListener('keydown', keydown);
    window.addEventListener('resize', resize);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', keydown);
      window.removeEventListener('resize', resize);
    };
  }, [mobileOpen]);

  // Only on the restaurant listings. Everywhere else — benefits, contact, the
  // profile, a single restaurant — there is nothing on screen for a location
  // to reorder, so the control would be a button that appears to do nothing.
  const showLocation = pathname === '/';

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  return (
    <>
      <header className={`navbar ${mobileOpen ? 'navbar--open' : ''}`}>
        <div className="navbar__inner">
          <Link to="/" className="navbar__logo-link" aria-label="Volcyy Home">
            <img src="/assets/logo.png" alt="Volcyy" className="navbar__logo" />
          </Link>

          <nav className="navbar__links" aria-label="Main navigation">
            <NavLink to="/" end className={() => onExplore ? 'active' : ''}>Explore</NavLink>
            {DISCOUNTS_ENABLED && <NavLink to="/benefits" className={({ isActive }) => isActive ? 'active' : ''}>Benefits</NavLink>}
            {user && <NavLink to="/profile" className={({ isActive }) => isActive ? 'active' : ''}>Profile</NavLink>}
            <NavLink to="/contact" className={({ isActive }) => isActive ? 'active' : ''}>Contact</NavLink>
            <NavLink to={isRestaurantOwner ? '/business/dashboard' : '/business'} className={() => inBusinessArea ? 'active' : ''}>
              For Businesses
            </NavLink>
            {isAdmin && (
              <NavLink to="/admin" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>Admin</NavLink>
            )}
          </nav>

          {showLocation && (
            <div className="navbar__location">
              <LocationPicker location={searchLocation} onChange={setSearchLocation} />
            </div>
          )}

          <div className="navbar__actions">
            {user ? (
              <>
                <span className="navbar__greeting">
                  Hello, {(userProfile?.displayName || user.displayName || '').split(' ')[0]}
                </span>
                <button className="navbar__btn navbar__btn--outline" onClick={handleLogout}>Sign Out</button>
              </>
            ) : guestWithPosts ? (
              <>
                <span className="navbar__greeting">{guestProfile.handle}</span>
                <button className="navbar__btn navbar__btn--primary" onClick={openClaim}>
                  Claim Account
                </button>
              </>
            ) : (
              <button className="navbar__btn navbar__btn--primary" onClick={() => setAuthModal('signin')}>
                Sign In
              </button>
            )}
          </div>

          <button
            ref={toggleRef}
            className="navbar__mobile-toggle"
            aria-controls="mobile-navigation"
            onClick={() => setMobileOpen(v => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        {mobileOpen && (
          <nav id="mobile-navigation" ref={menuRef} className="navbar__mobile-menu" aria-label="Mobile navigation">
            <NavLink to="/" end className={() => onExplore ? 'active' : ''} onClick={() => setMobileOpen(false)}>Explore</NavLink>
            {DISCOUNTS_ENABLED && <NavLink to="/benefits" onClick={() => setMobileOpen(false)}>Benefits</NavLink>}
            {user && <NavLink to="/profile" onClick={() => setMobileOpen(false)}>Profile</NavLink>}
            <NavLink to="/contact" onClick={() => setMobileOpen(false)}>Contact</NavLink>
            <NavLink to={isRestaurantOwner ? '/business/dashboard' : '/business'} className={() => inBusinessArea ? 'active' : ''} onClick={() => setMobileOpen(false)}>For Businesses</NavLink>
            {isAdmin && <NavLink to="/admin" onClick={() => setMobileOpen(false)}>Admin</NavLink>}
            <div className="navbar__mobile-divider" />
            {user ? (
              <>
                <span className="navbar__greeting">
                  Hello, {(userProfile?.displayName || user.displayName || '').split(' ')[0]}
                </span>
                <button onClick={() => { handleLogout(); setMobileOpen(false); }}>Sign Out</button>
              </>
            ) : guestWithPosts ? (
              <>
                <span className="navbar__greeting">Posting as {guestProfile.handle}</span>
                <button className="primary" onClick={() => { openClaim(); setMobileOpen(false); }}>Claim Account</button>
              </>
            ) : (
              <>
                <button className="outline" onClick={() => { setAuthModal('signin'); setMobileOpen(false); }}>Sign In</button>
                <button className="primary" onClick={() => { setAuthModal('signup'); setMobileOpen(false); }}>Sign Up</button>
              </>
            )}
          </nav>
        )}
      </header>

      {authModal && (
        <AuthModal defaultTab={authModal} onClose={() => { setAuthModal(null); if (routeState?.signIn) navigate(pathname, { replace: true, state: null }); }} onSuccess={() => { setAuthModal(null); if (routeState?.returnTo) navigate(routeState.returnTo, { replace: true }); }} />
      )}
    </>
  );
}
